<template>
  <div
    class="language-selector"
    ref="selectorRef"
    v-if="languageList.length > 1"
  >
    <div class="language-trigger" @click="toggleDropdown">
      <span class="language-text">{{ currentLanguageName }}</span>
      <img
        src="@/assets/arrow-down.png"
        alt="arrow"
        class="arrow-icon"
        :class="{ 'arrow-up': showDropdown }"
      />
    </div>
    <transition name="dropdown">
      <div v-if="showDropdown" class="language-dropdown">
        <div
          v-for="lang in languageList"
          :key="lang.code"
          class="language-item"
          :class="{ active: isActive(lang) }"
          @click="selectLanguage(lang)"
        >
          {{ lang.name }}
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useLanguageStore } from "@/stores/language";
import { setI18nLocale } from "@/i18n";
import { useForumStore, GAME_ID } from "@/stores";
const forumStore = useForumStore();
const languageStore = useLanguageStore();
const showDropdown = ref(false);
const selectorRef = ref<HTMLElement | null>(null);

// 当前语言代码（使用 curLanguage，可能是 "all"）
const currentLanguage = computed(() => {
  return languageStore.curLanguage;
});

// 语言列表
const languageList = computed(() => {
  return languageStore.languageList.length > 0
    ? languageStore.languageList
    : [];
});

// 当前语言显示名称
const currentLanguageName = computed(() => {
  const currentLang = languageList.value.find(
    (lang) => lang.code === currentLanguage.value
  );
  return currentLang?.name || currentLanguage.value;
});

// 获取语言代码
const getLanguageCode = (lang: { code?: string }) => {
  return lang.code || "";
};

// 判断是否为当前选中的语言
const isActive = (lang: { code?: string }) => {
  const code = getLanguageCode(lang);
  return code === currentLanguage.value;
};

// 切换下拉菜单
const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

// 选择语言
const selectLanguage = (lang: { code?: string }) => {
  const code = getLanguageCode(lang);
  if (code && code !== currentLanguage.value) {
    if (code === "all") {
      // 选择"全部"时：
      // 1. curLanguage 设置为 "all"（用于下拉框显示和筛选），setLanguage 会自动保存到 localStorage
      languageStore.setLanguage("all");
      // 2. locale 设置为默认语言（用于 i18n 界面语言）
      setI18nLocale(languageStore.defaultLanguage);
    } else {
      // 选择具体语言时，同时更新 locale 和 curLanguage，setLanguage 会自动保存到 localStorage
      setI18nLocale(code);
      languageStore.setLanguage(code);
    }
    forumStore.getForumInfo();
  }
  showDropdown.value = false;
};

// 点击外部关闭下拉菜单
const handleClickOutside = (event: MouseEvent) => {
  if (selectorRef.value && !selectorRef.value.contains(event.target as Node)) {
    showDropdown.value = false;
  }
};

// 监听语言列表变化，确保当前语言在列表中
watch(
  () => languageStore.languageList,
  () => {
    if (languageStore.languageList.length > 0) {
      const codes = languageStore.languageList.map((lang) =>
        getLanguageCode(lang)
      );
      const currentCode = currentLanguage.value;
      // 如果当前语言不在列表中（且不是 "all"），使用默认语言
      if (currentCode !== "all" && !codes.includes(currentCode)) {
        // 如果语言列表长度大于1，默认选中"全部"
        if (languageStore.languageList.length > 1) {
          languageStore.setLanguage("all");
        } else {
          setI18nLocale(languageStore.defaultLanguage);
          languageStore.setLanguage(languageStore.defaultLanguage);
        }
      }
    }
  },
  { immediate: true }
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped lang="scss">
.language-selector {
  position: relative;
  z-index: 10000;
}

.language-trigger {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  border-radius: 16px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  &:active {
    transform: scale(0.98);
  }

  .language-text {
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  }

  .arrow-icon {
    width: 12px;
    height: 12px;
    transition: transform 0.2s ease;
    filter: brightness(0) invert(1);

    &.arrow-up {
      transform: rotate(180deg);
    }
  }
}

.language-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 140px;
  max-height: 300px;
  overflow-y: auto;
  background: rgba(40, 40, 40, 0.9);
  backdrop-filter: blur(8px);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  z-index: 10001;
  padding: 4px 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }
}

.language-item {
  padding: 10px 16px;
  font-size: 14px;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  &:hover,
  &:active {
    background: rgba(255, 255, 255, 0.15);
  }

  &.active {
    color: #ffd700;
    font-weight: 600;
    background: rgba(255, 215, 0, 0.15);
  }
}

// 下拉动画
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
